import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<RecoveryView />` — Smart Health Account recovery flow.
 *
 * Phase A scaffolding per design memo PR #136. Drives the
 * `IRecoveryFlow` orchestrator + dispatches step views by current
 * state. Cryptographic mechanism integration (recovery-passphrase
 * derivation; threshold reconstruction; PMK unwrap) is stubbed
 * pending Issue #48 counsel disposition + DEV3 backend
 * implementation per design memo §5 phase split.
 *
 * **Doctrinal load-bearing posture (encoded structurally):**
 *   - Recovery is patient-facing-account-control; non-delegable
 *     fiduciary authority (ADR-026 §3). UX always PBC-operated.
 *   - **Recovery UX honesty is doctrinally binding** (Doctrine v1.3
 *     §1 + §8 — Bundle B.3 ELEVATE ratified 2026-05-06; second
 *     non-negotiability marker alongside §6 Encryption / §8 Patient
 *     custody NO-SHT-BACKDOOR). "Content unrecoverable" framing must
 *     survive UX simplification; v1 ships layered controls only;
 *     binding-through-substrate-doctrine, not binding-through-CTO-
 *     authority. Memory `feedback_phg_recovery_ux_honesty` carries
 *     the application discipline; doctrine carries the commitment.
 *   - Recovery ≠ erasure (ADR-024 §2.7.5 + Doctrine v1.3 §8 Bundle
 *     B.2): failure-unrecoverable terminal state offers reactivate-
 *     from-holders pathway; never routes to erasure UI. Inverse of
 *     `<ErasureCompositionStep />` framing. Doctrine v1.3 §8 commits
 *     the strict separation: "Recovery-failure scenarios … are
 *     explicitly NOT erasure."
 *   - NO SHT BACKDOOR carries through: recovery passphrase NEVER
 *     leaves the client (Tech Spec §6.2.3 + scope memo §5.2 +
 *     ADR-032 §1 + Doctrine v1.3 §6 Encryption / §8 Patient custody
 *     A.4/B.1 first non-negotiability marker).
 *
 * **Patient-Facing Terms v1.3 doctrinal-copy gate** (coord issue
 * #42). Recovery copy is placeholder until v1.3 lands; views
 * structured for mechanical swap.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { RecoveryCancelledStep } from './_recovery/cancelled-step.js';
import { RecoveryCoolingOffStep } from './_recovery/cooling-off-step.js';
import { RecoveryErrorStep } from './_recovery/error-step.js';
import { RecoveryFailureRecoverableStep } from './_recovery/failure-recoverable-step.js';
import { RecoveryFailureUnrecoverableStep } from './_recovery/failure-unrecoverable-step.js';
import { RecoveryIntroStep } from './_recovery/intro-step.js';
import { RecoveryPassphraseEntryStep } from './_recovery/passphrase-entry-step.js';
import { RecoverySuccessStep } from './_recovery/success-step.js';
import { RecoveryThresholdReconstructStep } from './_recovery/threshold-reconstruct-step.js';
import { RecoveryTierElectionStep } from './_recovery/tier-election-step.js';
import { RecoveryVerificationLayersStep } from './_recovery/verification-layers-step.js';
function useRecoveryFlowState(flow) {
    const [state, setState] = React.useState(() => flow.getState());
    React.useEffect(() => {
        return flow.subscribe(setState);
    }, [flow]);
    return state;
}
export function RecoveryView(props) {
    const { t } = useLocale();
    if (props.flow === undefined) {
        return (_jsxs("section", { "aria-labelledby": "recovery-heading", children: [_jsx("h2", { id: "recovery-heading", children: t('recovery.heading') }), _jsx("p", { children: t('recovery.body_1') }), _jsx("p", { children: t('recovery.body_2') }), _jsx("p", { children: _jsx("em", { children: t('recovery.unwired_notice') }) })] }));
    }
    return (_jsx(RecoveryViewWired, { flow: props.flow, ...(props.onRestartRequested !== undefined
            ? { onRestartRequested: props.onRestartRequested }
            : {}) }));
}
function RecoveryViewWired(props) {
    const { t } = useLocale();
    const state = useRecoveryFlowState(props.flow);
    const handleRestart = () => {
        if (props.onRestartRequested !== undefined) {
            props.onRestartRequested();
        }
        else {
            props.flow.restart();
        }
    };
    return (_jsxs("section", { "aria-labelledby": "recovery-heading", children: [_jsx("h2", { id: "recovery-heading", children: t('recovery.heading') }), renderStep(state, props.flow, handleRestart)] }));
}
function renderStep(state, flow, handleRestart) {
    switch (state.kind) {
        case 'intro':
            return (_jsx(RecoveryIntroStep, { onBegin: () => flow.beginTierElection(), onCancel: () => flow.cancel('user-cancelled-at-intro') }));
        case 'tier-election':
            return (_jsx(RecoveryTierElectionStep, { onSubmitTier: (tier) => {
                    void flow.submitTier(tier);
                }, onCancel: () => flow.cancel('user-cancelled-at-tier-election') }));
        case 'verification-layers':
            return (_jsx(RecoveryVerificationLayersStep, { layerStates: state.layerStates, onSubmitLayer: (opts) => {
                    void flow.submitLayer(opts);
                }, onCancel: () => flow.cancel('user-cancelled-at-verification-layers') }));
        case 'recovery-passphrase':
            return (_jsx(RecoveryPassphraseEntryStep, { onSubmit: (passphrase) => {
                    void flow.submitPassphrase(passphrase);
                }, onCancel: () => flow.cancel('user-cancelled-at-passphrase') }));
        case 'threshold-reconstruct':
            return _jsx(RecoveryThresholdReconstructStep, {});
        case 'cooling-off-active':
            return (_jsx(RecoveryCoolingOffStep, { tier: state.tier, secondsRemaining: state.secondsRemaining, coolOffUntil: state.coolOffUntil }));
        case 'success':
            return _jsx(RecoverySuccessStep, { tier: state.tier });
        case 'failure-recoverable':
            return (_jsx(RecoveryFailureRecoverableStep, { escalationPathway: state.escalationPathway, reason: state.reason, onRestart: handleRestart }));
        case 'failure-unrecoverable':
            return _jsx(RecoveryFailureUnrecoverableStep, { onClose: handleRestart });
        case 'cancelled':
            return _jsx(RecoveryCancelledStep, { reason: state.reason, onRestart: handleRestart });
        case 'error':
            return (_jsx(RecoveryErrorStep, { code: state.code, message: state.message, onRestart: handleRestart }));
    }
}
//# sourceMappingURL=recovery-view.js.map